//@ts-nocheck
import React, { forwardRef } from 'react';
import {
  Typography as MuiTypography,
  StyledEngineProvider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { classNames } from 'utilities/Utils';
import { theme, getColor, PaletteColors } from 'app/latest-theme';

type ThemeType = typeof theme;

// type defination for typography

const MuiTypographyVariants = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'caption',
  'button',
  'overline',
];

export type VariantType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'caption'
  | 'button'
  | 'overline'
  | 'subHeadingRegular'
  | 'subHeadingMedium'
  | 'buttonText'
  | 'sectionHeading'
  | 'subtextMedium'
  | 'legends'
  | 'shoutValue'
  | 'legendsRegular';

type MuiTypographyProps =
  | Pick<
  React.ComponentProps<typeof MuiTypography>,
  | 'ref'
  | 'align'
  | 'children'
  | 'children'
  | 'classes'
  | 'color'
  | 'display'
  | 'gutterBottom'
  | 'noWrap'
  | 'paragraph'
  | 'variantMapping'
  | 'onClick'
  > & {
    variant?: VariantType;
  };

type Props = MuiTypographyProps & {
  className?: string;
  style?: React.CSSProperties;
  fb?: boolean;
  themeColor?: PaletteColors;
  themeBg?: PaletteColors;
  inline?: boolean;
  isLink?: boolean;
  overflowWrap?: boolean;
  title?: string;
};
// end of type defination for typography

const commonTypography = {
  fontStyle: 'normal',
  color: '#0F0F0F',
};

const useStyles = makeStyles<
ThemeType,
{ themeColor: PaletteColors; themeBg: PaletteColors; fb: boolean }
>(() => ({
  root: {
    fontWeight: ({ fb }) => (fb ? 500 : 0),
  },
  overflowWrap: {
    overflowWrap: 'anywhere',
  },
  addColor: {
    color: ({ themeColor }) => (themeColor ? getColor(themeColor) : null),
    backgroundColor: ({ themeBg }) => (themeBg ? getColor(themeBg) : null),
  },
  inline: {
    display: 'inline',
  },
  link: {
    cursor: 'pointer',
  },
  h1: {
    ...commonTypography,
    fontWeight: 700,
    fontSize: '28px',
    lineHeight: '42px',
  },
  h2: {
    ...commonTypography,
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
  },
  h3: {
    ...commonTypography,
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '27px',
  },
  subHeadingRegular: {
    ...commonTypography,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '20px',
  },
  subHeadingMedium: {
    ...commonTypography,
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '20px',
  },
  body1: {
    ...commonTypography,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
  body2: {
    ...commonTypography,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
  },
  buttonText: {
    ...commonTypography,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '18px',
    letterSpacing: '0.01em',
    textTransform: 'uppercase',
  },
  sectionHeading: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#787878',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  subtitle1: {
    // subtext regular
    ...commonTypography,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.02em',
  },
  subtitle2: {
    ...commonTypography,
    fontSize: '10px',
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.02em',
  },
  subtextMedium: {
    ...commonTypography,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  legends: {
    ...commonTypography,
    fontSize: '10px',
    lineHeight: '14px',
    letterSpacing: '0.02em',
  },
  legendsRegular: {
    ...commonTypography,
    fontSize: '10px',
    lineHeight: '11.72px',
    letterSpacing: '0.02em',
  },
  shoutValue: {
    ...commonTypography,
    fontSize: '32px',
    lineHeight: '42px',
    letterSpacing: '0.02em',
  },
}));

const Typography = (props: Props, ref) => {
  const {
    variant = 'body1',
    className = '',
    themeColor = 'text.primary',
    themeBg,
    fb = false,
    onClick,
    inline,
    isLink,
    //TODO: Replace with BoxProps
    overflowWrap,
    ...rest
  } = props;
  const classes = useStyles({ themeColor, themeBg, fb });
  const isMuiVariant = MuiTypographyVariants.includes(variant);
  return (
    <StyledEngineProvider injectFirst>
      <MuiTypography
        ref={ref}
        className={classNames(
          classes.root,
          className,
          classes.addColor,
          inline && classes.inline,
          isLink && classes.link,
          overflowWrap && classes.overflowWrap,
          classes[variant],
        )}
        variant={isMuiVariant ? variant : 'body1'}
        onClick={onClick}
        {...rest}
      />
    </StyledEngineProvider>
  );
};

export default forwardRef(Typography);
